import { useEffect, useRef, useState } from 'react';
import { GridHalf, PageGrid, SectionLabel } from '@/components/base/Grid';
import LocalMap from '@/components/feature/LocalMap';
import { focusAreas, phases, signals } from '@/mocks/content';

export function Approach() {
  const [active, setActive] = useState(0);
  const refs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActive(Number((e.target as HTMLElement).dataset.i));
      });
    }, { rootMargin: '-45% 0px -45% 0px' });
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <PageGrid id="approach" label="Approach" dark className="rev bg-primary-500 text-background-50">
      <GridHalf>
        <ol className="h-full grid">
          {phases.map((p, i) => (
            <li
              key={p.n}
              data-i={i}
              ref={(el) => { refs.current[i] = el; }}
              className={`relative px-cell py-10 lg:py-12 lg:min-h-[42svh] flex flex-col justify-center border-t rule-dark first:border-t-0 transition-[opacity,background-color] duration-500 ${active === i ? 'opacity-100 bg-background-50/[0.04]' : 'lg:opacity-60'}`}
            >
              <span className={`absolute left-0 top-0 h-px bg-accent-500 transition-all duration-700 ${active === i ? 'w-full' : 'w-0'}`} />
              <div className="grid grid-cols-[56px_minmax(0,1fr)] gap-x-4">
                <span className="t-label text-background-400 pt-2 flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 transition-colors duration-500 ${active === i ? 'bg-accent-500' : 'bg-background-50/30'}`} />{p.n}
                </span>
                <div>
                  <h3 className="font-heading text-[28px] lg:text-[36px] leading-none tracking-[-0.03em] font-medium">{p.title}</h3>
                  <p className="mt-4 text-[15px] lg:text-[16px] text-background-200 leading-relaxed max-w-[400px]">{p.text}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </GridHalf>
      <GridHalf side="r">
        <div className="stick flex flex-col">
          <div className="cell !pb-6 flex flex-col gap-10">
            <SectionLabel index="03" dark>Our method</SectionLabel>
            <div className="reveal">
              <h2 className="t-edit">Clarity before complexity.</h2>
              <p className="mt-6 t-body text-background-300 max-w-[420px]">A simple, repeatable rhythm. We fix what matters most first, then keep improving with evidence.</p>
            </div>
          </div>
          <div className="hidden lg:grid grid-cols-2 items-end px-cell pb-12">
            <div>
              <span key={active} className="block font-heading text-[clamp(72px,7vw,112px)] leading-[0.9] tracking-[-0.05em] text-accent-500 intro">0{active + 1}</span>
              <span className="mt-3 block t-label text-background-300">/ 04 — {phases[active].title}</span>
            </div>
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 200 200" className="w-full max-w-[180px]" aria-hidden="true">
                <g className="orbit-mini" style={{ transformOrigin: '100px 100px' }} fill="none" stroke="currentColor" strokeOpacity="0.35">
                  <ellipse cx="100" cy="100" rx="90" ry="32" />
                  <ellipse cx="100" cy="100" rx="90" ry="32" transform="rotate(60 100 100)" />
                  <ellipse cx="100" cy="100" rx="90" ry="32" transform="rotate(120 100 100)" />
                </g>
                {phases.map((p, i) => {
                  const a = (i / 4) * Math.PI * 2 - Math.PI / 2;
                  return (
                    <circle key={p.n} cx={100 + Math.cos(a) * 60} cy={100 + Math.sin(a) * 60} r={i === active ? 6 : 3.5}
                      className={`transition-all duration-500 ${i === active ? 'fill-accent-500' : 'fill-background-50/50'}`} />
                  );
                })}
                <circle cx="100" cy="100" r="4" className="fill-accent-500" />
              </svg>
            </div>
          </div>
        </div>
      </GridHalf>
    </PageGrid>
  );
}

export function Focus() {
  return (
    <PageGrid id="focus" label="Focus" className="bg-white">
      <GridHalf className="flex flex-col bg-white">
        <div className="cell flex-1 flex flex-col justify-between gap-10">
          <SectionLabel index="04">Where it counts</SectionLabel>
          <h2 className="t-h2 max-w-[520px] reveal">Built for businesses people search for.</h2>
        </div>
        <ul>
          {focusAreas.map((f, i) => (
            <li key={f.n} className="group border-t rule px-cell py-7 lg:py-8 reveal" style={{ transitionDelay: `${i * 70}ms` }}>
              <div className="grid grid-cols-[40px_minmax(0,1fr)] gap-x-4">
                <span className="t-label text-foreground-600 pt-1.5 group-hover:text-accent-700 transition-colors">{f.n}</span>
                <div>
                  <h3 className="t-h3">{f.title}</h3>
                  <p className="mt-2 text-[15px] text-foreground-800 leading-relaxed">{f.text}</p>
                  <p className="mt-2 text-[14px] text-foreground-600 leading-relaxed">{f.need}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </GridHalf>
      <GridHalf side="r" className="relative flex flex-col bg-white">
        <div className="relative flex-1 min-h-[380px] md:min-h-[520px] lg:min-h-[600px] overflow-hidden">
          <div data-parallax="0.05" data-scale="1.08" className="absolute inset-0 will-change-transform">
            <LocalMap />
          </div>
        </div>
        <div className="bg-white px-cell py-3.5 flex justify-between gap-4 t-label text-foreground-700 border-t rule">
          <span>Fig. 03 — Example radius · SoHo, Manhattan</span>
          <span className="hidden sm:inline">Map data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline cursor-pointer">OpenStreetMap</a> contributors</span>
        </div>
      </GridHalf>
    </PageGrid>
  );
}

export function Signals() {
  return (
    <PageGrid label="What we measure" className="rev">
      <GridHalf className="flex flex-col bg-white">
        <ol className="flex-1 flex flex-col">
          {signals.map((s, i) => (
            <li
              key={s.n}
              className={`group flex-1 px-cell py-8 lg:py-10 lg:min-h-[240px] border-t rule first:border-t-0 flex flex-col justify-between gap-8 transition-colors duration-300 ${i === 3 ? 'bg-accent-500' : 'hover:bg-background-50'}`}
            >
              <div className="flex items-center justify-between gap-4 reveal">
                <span className={`t-label ${i === 3 ? 'text-accent-950' : 'text-foreground-600'}`}>Signal {s.n}</span>
                <span className="flex items-center gap-1" aria-hidden="true">
                  {[0, 1, 2, 3].map((k) => (
                    <span key={k} className={`h-[3px] w-6 ${k <= i ? (i === 3 ? 'bg-foreground-950' : 'bg-accent-600') : (i === 3 ? 'bg-foreground-950/20' : 'bg-background-200')}`} />
                  ))}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 items-end reveal">
                <h3 className="t-h3">{s.title}</h3>
                <p className={`text-[15px] leading-relaxed ${i === 3 ? 'text-foreground-900' : 'text-foreground-700'}`}>{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </GridHalf>
      <GridHalf side="r" className="flex flex-col">
        <div className="bg-white flex-1">
          <div className="stick cell flex flex-col gap-10">
            <SectionLabel index="05">What we measure</SectionLabel>
            <div className="reveal">
              <h2 className="t-h2">Less noise.<br />Better signals.</h2>
              <p className="mt-5 t-body text-foreground-700 max-w-[420px]">We report on the path from being seen to being contacted, not vanity numbers. Each signal tells us where to act next.</p>
            </div>
          </div>
        </div>
      </GridHalf>
    </PageGrid>
  );
}